import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Plus, Search, Package, Sparkles, TrendingUp, Clock } from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/apiClient";
import { getGuestToken } from "@/lib/guestToken";

const CATEGORIES = [
  "Imported Drinks", "Chocolates & Sweets", "Chips & Snacks", "Instant Foods",
  "Biscuits & Bakery", "Ice Cream & Frozen Treats", "Novelty Products", "Other Products",
];
const SORTS = [
  { k: "most_requested", l: "Most Wanted" },
  { k: "most_liked", l: "Most Liked" },
  { k: "recently_approved", l: "Recently Approved" },
  { k: "newest_activity", l: "Newest Activity" },
];
const STATUS_LABEL = {
  gathering_interest: { l: "Gathering Interest", c: "bg-amber-100 text-amber-800" },
  under_consideration: { l: "Under Consideration", c: "bg-sky-100 text-sky-800" },
  sourcing: { l: "Sourcing", c: "bg-blue-100 text-blue-800" },
  ordered: { l: "Ordered", c: "bg-purple-100 text-purple-800" },
  available_in_store: { l: "Available in Store", c: "bg-emerald-100 text-emerald-800" },
  currently_unavailable: { l: "Currently Unavailable", c: "bg-slate-200 text-slate-700" },
};

export default function RequestedProducts() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [sort, setSort] = useState("most_requested");
  const [loading, setLoading] = useState(true);
  const [likedMap, setLikedMap] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wishlist/products", { params: { q, category, sort, limit: 60 } });
      setItems(data.items); setTotal(data.total);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [sort, category]);
  useEffect(() => {
    try { setLikedMap(JSON.parse(localStorage.getItem("pp_wl_liked") || "{}")); } catch { /* ignore */ }
  }, []);

  const toggleLike = async (p) => {
    try {
      const { data } = await api.post(`/wishlist/products/${p.id}/like`, { guest_token: getGuestToken() });
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, like_count: data.like_count } : x));
      const nm = { ...likedMap, [p.id]: data.liked };
      setLikedMap(nm); localStorage.setItem("pp_wl_liked", JSON.stringify(nm));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const requestToo = async (p) => {
    try {
      const { data } = await api.post(`/wishlist/products/${p.id}/request-too`, { guest_token: getGuestToken() });
      toast.success(`Wish added! Your tracker: ${data.tracking_code}`, { duration: 8000 });
      try {
        const stored = JSON.parse(localStorage.getItem("pp_my_requests") || "[]");
        const entry = { ref: data.public_ref, code: data.tracking_code, name: p.name, at: new Date().toISOString() };
        localStorage.setItem("pp_my_requests", JSON.stringify([entry, ...stored.filter(x => x.ref !== data.public_ref)].slice(0, 10)));
      } catch { /* ignore */ }
      setItems((prev) => prev.map((x) => x.id === p.id ? { ...x, request_count: x.request_count + 1 } : x));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#FFF8F0]" data-testid="requested-products-page">
      <div className="max-w-6xl mx-auto px-4 py-10">
        <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div className="max-w-xl">
            <div className="inline-block px-3 py-1 rounded-full bg-black text-[#FEED02] text-[10px] font-black tracking-[0.25em] mb-4">COMMUNITY WISHLIST</div>
            <h1 className="font-display font-black text-black leading-[0.9]" style={{ fontSize: "clamp(44px, 10vw, 72px)" }}>THE PEOPLE<br />HAVE SPOKEN.</h1>
            <p className="text-black/70 mt-3 max-w-md text-base">See what's on everyone's wishlist. Add yours — the louder the demand, the sooner it hits our shelves.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/track-request" className="btn-pill btn-ghost text-xs" data-testid="btn-track-wishlist">Track Request</Link>
            <Link to="/request-product" className="btn-pill btn-dark text-xs" data-testid="btn-add-request"><Plus size={12} /> Request a Product</Link>
          </div>
        </header>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-brand-line p-4 mb-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search products or brands…" className="pp-input pl-9" data-testid="wl-search" />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="pp-input w-auto" data-testid="wl-category">
            <option value="">All Categories</option>
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} className="pp-input w-auto" data-testid="wl-sort">
            {SORTS.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-center text-brand-charcoal/50 py-20">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-3xl border border-brand-line p-14 text-center">
            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#FEED02]/50 grid place-items-center"><Sparkles size={24} /></div>
            <h3 className="font-display text-2xl mb-2">Your next favourite isn't here yet.</h3>
            <p className="text-brand-charcoal/60 mb-5">Be the first to request it!</p>
            <Link to="/request-product" className="btn-pill btn-dark text-xs" data-testid="empty-cta">Request a Product</Link>
          </div>
        ) : (
          <>
            <div className="text-xs text-brand-charcoal/55 mb-4">{total} product{total !== 1 ? "s" : ""} on the wishlist</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map((p) => <ProductCard key={p.id} p={p} liked={!!likedMap[p.id]} onLike={() => toggleLike(p)} onRequestToo={() => requestToo(p)} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function ProductCard({ p, liked, onLike, onRequestToo }) {
  const sl = STATUS_LABEL[p.public_status] || STATUS_LABEL.gathering_interest;
  const imgUrl = p.image_path ? `${API_BASE}/wishlist/images/${p.image_path}` : null;
  return (
    <div className="bg-white rounded-2xl border border-brand-line overflow-hidden flex flex-col hover:shadow-md transition-shadow" data-testid={`wl-card-${p.slug}`}>
      <Link to={`/requested-products/${p.slug}`} className="block aspect-[4/3] bg-[#FEED02]/10 grid place-items-center overflow-hidden">
        {imgUrl ? <img src={imgUrl} alt={p.name} className="w-full h-full object-cover" loading="lazy" /> : <Package size={40} className="text-brand-charcoal/25" />}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="min-w-0">
            <Link to={`/requested-products/${p.slug}`} className="font-display text-lg leading-tight hover:underline block truncate">{p.name}</Link>
            {(p.brand || p.variant) && <div className="text-xs text-brand-charcoal/55 truncate">{p.brand}{p.brand && p.variant ? " · " : ""}{p.variant}</div>}
          </div>
          <button onClick={onLike} className={`shrink-0 w-9 h-9 rounded-full grid place-items-center transition ${liked ? "bg-red-100 text-red-600" : "bg-brand-muted/50 text-brand-charcoal/50 hover:bg-brand-muted"}`} data-testid={`like-${p.slug}`} aria-label="Like">
            <Heart size={15} fill={liked ? "currentColor" : "none"} />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap mb-3 text-[10px]">
          <span className={`px-2 py-0.5 rounded-full font-medium ${sl.c}`}>{sl.l}</span>
          <span className="text-brand-charcoal/50">{p.category}</span>
        </div>
        <div className="mt-auto flex items-center justify-between pt-3 border-t border-brand-line">
          <div className="flex items-center gap-3 text-xs text-brand-charcoal/70">
            <span className="inline-flex items-center gap-1"><TrendingUp size={11} /> {p.request_count} want</span>
            <span className="inline-flex items-center gap-1"><Heart size={11} /> {p.like_count}</span>
          </div>
          <button onClick={onRequestToo} className="btn-pill text-[11px] bg-[#FEED02] text-brand-charcoal hover:bg-[#FEED02]/80" data-testid={`req-too-${p.slug}`}>I Want This</button>
        </div>
      </div>
    </div>
  );
}
